import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { SCTooltip } from 'rollup-allspark';
import BusinessContext from './components/BusinessContext';
import Composer from './components/Composer';
import ConversationSwitcher from './components/ConversationSwitcher';
import MessageList from './components/MessageList';
import UsageMeter from './components/UsageMeter';
import useKaiChat from './useKaiChat';

const QuotaBanner = ({ usage }) => (
  <div className="border-t border-red-lighter bg-red-lighter p-4 text-center">
    <p className="text-sm font-medium text-red-dark">
      Monthly question limit reached
    </p>
    <p className="mt-1 text-xs text-grey-darker">
      {usage
        ? `You've used all ${usage.questions_limit} questions for ${usage.month}. The limit resets next month.`
        : 'The limit resets next month.'}
    </p>
  </div>
);

QuotaBanner.propTypes = { usage: PropTypes.object };

/**
 * Reusable kAI chat surface — header (usage meter + conversation switcher +
 * new chat), business scope, message list and composer. Used both inside the
 * drawer and by the retired /kai route as a full-page wrapper. All state comes
 * from the global kai store via useKaiChat, so it survives close + navigation.
 */
const KaiChat = ({ onClose, fullPage, active }) => {
  const kai = useKaiChat(active);
  const [text, setText] = useState('');
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const {
    messages,
    isStreaming,
    activeTool,
    historyLoading,
    conversations,
    conversationsLoading,
    conversationId,
    businesses,
    businessId,
    usage,
    quotaExceeded,
    errorMessage,
    send,
    retry,
    cancel,
    newChat,
    openConversation,
    setBusinessId,
    loadConversations,
  } = kai;

  const atLimit =
    usage &&
    usage.enabled !== false &&
    usage.questions_limit > 0 &&
    (usage.questions_used ?? 0) >= usage.questions_limit;

  const disabled = !businessId || usage?.enabled === false || atLimit;
  const disabledReason = (() => {
    if (!businessId) return 'Pick a business first';
    if (usage?.enabled === false) return 'kAI is disabled for this business';
    if (atLimit) return 'Monthly question limit reached';
    return undefined;
  })();

  const handleSend = () => {
    const q = text.trim();
    if (!q) return;
    setText('');
    send(q);
  };

  const toggleSwitcher = () => {
    const next = !switcherOpen;
    setSwitcherOpen(next);
    if (next) loadConversations(businessId);
  };

  const handleSelectConversation = id => {
    setSwitcherOpen(false);
    openConversation(id);
  };

  const handleNewChat = () => {
    setSwitcherOpen(false);
    newChat();
  };

  return (
    <div className="flex h-full flex-col bg-grey-lighter">
      {/* Header */}
      <div className="relative">
        <div className="flex items-center justify-between gap-x-2 border-b border-grey px-4 py-3">
          <div className="flex items-center gap-x-2">
            <i className="kt-sparkles text-lg text-primary" />
            <span className="text-base font-semibold text-black">kAI</span>
          </div>
          <div className="flex items-center gap-x-2">
            <UsageMeter usage={usage} />
            <SCTooltip content="Past conversations">
              <button
                type="button"
                onClick={toggleSwitcher}
                aria-label="Past conversations"
                className="rounded-md p-1 text-grey-darker hover:bg-grey-light"
              >
                <i className="kt-history" />
              </button>
            </SCTooltip>
            <SCTooltip content="New conversation">
              <button
                type="button"
                onClick={handleNewChat}
                aria-label="New conversation"
                className="rounded-md p-1 text-grey-darker hover:bg-grey-light"
              >
                <i className="kt-add" />
              </button>
            </SCTooltip>
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-md p-1 text-grey-darker hover:bg-grey-light"
              >
                <i className="sc-clear" />
              </button>
            ) : null}
          </div>
        </div>

        <ConversationSwitcher
          open={switcherOpen}
          conversations={conversations}
          loading={conversationsLoading}
          activeId={conversationId}
          onSelect={handleSelectConversation}
          onNewChat={handleNewChat}
        />
      </div>

      <BusinessContext
        businesses={businesses}
        businessId={businessId}
        onChange={setBusinessId}
        disabled={isStreaming}
      />

      {/* Messages */}
      <div
        className={`flex flex-1 flex-col overflow-hidden ${
          fullPage ? 'mx-auto w-full max-w-3xl' : ''
        }`}
      >
        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          activeTool={activeTool}
          historyLoading={historyLoading}
          onRetry={retry}
          onSuggestion={q => {
            if (isStreaming) return;
            setText('');
            send(q);
          }}
        />

        {errorMessage ? (
          <div className="border-t border-red-lighter bg-red-lighter px-4 py-2 text-center text-xs text-red-dark">
            {errorMessage}
          </div>
        ) : null}

        {quotaExceeded || atLimit ? (
          <QuotaBanner usage={usage} />
        ) : (
          <Composer
            value={text}
            onChange={setText}
            onSubmit={handleSend}
            onCancel={cancel}
            isStreaming={isStreaming}
            disabled={disabled}
            disabledReason={disabledReason}
          />
        )}
      </div>
    </div>
  );
};

KaiChat.propTypes = {
  onClose: PropTypes.func,
  fullPage: PropTypes.bool,
  active: PropTypes.bool,
};

export default KaiChat;
